import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { MdLocalOffer } from "react-icons/md";
import axios from "../../config/axiosInstance";
import { COUPONS, EVENTS } from "../../config/apiConfig";
import Modal from "../modals/Modal";
import Drawer from "../Drawer";
import CouponForm from "../forms/CouponForm";
import { Badge, Card } from "../crm/Primitives";
import { formatDate, formatMoney, formatNumber } from "../crm/crmHelpers";
import { couponStatus, formatCouponDiscount } from "./eventHelpers";

/*
  O cupom do evento, administrado sem sair da tela do evento.

  O cupom é o mesmo da tela Cupons (mesmos endpoints e o mesmo formulário), só
  que daqui dá para criá-lo já amarrado ao evento, pausar, editar e ver quem
  resgatou. Um cupom serve a um evento só, e quem cadastra com ele vira cliente
  do evento (a qualquer tempo), então o código é também uma forma de medir.

  Soltar o cupom do evento não apaga o cupom: ele segue na tela Cupons.
*/

const getErrorMessage = (error) =>
  error?.response?.data?.message || error?.message || "Não foi possível concluir.";

const EventCouponCard = ({ event }) => {
  const queryClient = useQueryClient();
  const { coupon, metrics } = event;

  // "create" | "edit" | "pick" | null
  const [mode, setMode] = useState(null);
  const [formError, setFormError] = useState("");
  const [pickedId, setPickedId] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [notice, setNotice] = useState(null);

  const options = useQuery(
    ["events", "options"],
    async () => {
      const { data } = await axios.get(EVENTS.OPTIONS);
      return data.data;
    },
    { enabled: mode === "pick" }
  );

  const history = useQuery(
    ["platform-coupon-history", coupon?.id],
    async () => {
      const { data } = await axios.get(`${COUPONS.ONE}/${coupon.id}/history`);
      return data.data;
    },
    { enabled: historyOpen && Boolean(coupon?.id) }
  );

  const refresh = () => {
    queryClient.invalidateQueries(["events"]);
    queryClient.invalidateQueries("platform-coupons");
    queryClient.invalidateQueries(["platform-coupon-history"]);
  };

  const setEventCoupon = (platformCouponId) =>
    axios.put(`${EVENTS.ONE}/${event.id}`, { platformCouponId });

  const close = () => {
    setMode(null);
    setFormError("");
    setPickedId("");
  };

  const create = useMutation(
    async (payload) => {
      const { data } = await axios.post(COUPONS.LIST, payload);
      const created = data.data || data;

      try {
        await setEventCoupon(created.id);
      } catch (error) {
        // O cupom já existe; sem este aviso um novo "Criar" bateria em "já
        // existe um cupom com este código" sem explicar.
        throw new Error(
          `O cupom ${created.code} foi criado, mas não ficou neste evento (${getErrorMessage(
            error
          )}). Use "Usar um cupom que já existe".`
        );
      }
      return created;
    },
    {
      onSuccess: () => {
        refresh();
        close();
      },
      onError: (error) => setFormError(getErrorMessage(error)),
    }
  );

  const edit = useMutation(
    (payload) => axios.put(`${COUPONS.ONE}/${coupon.id}`, payload),
    {
      onSuccess: () => {
        refresh();
        close();
      },
      onError: (error) => setFormError(getErrorMessage(error)),
    }
  );

  const toggle = useMutation(
    () => axios.put(`${COUPONS.ONE}/${coupon.id}`, { isActive: !coupon.isActive }),
    {
      onSuccess: refresh,
      onError: (error) => setNotice(getErrorMessage(error)),
    }
  );

  const attach = useMutation((id) => setEventCoupon(id), {
    onSuccess: () => {
      refresh();
      close();
    },
    onError: (error) => setFormError(getErrorMessage(error)),
  });

  const detach = useMutation(() => setEventCoupon(null), {
    onSuccess: refresh,
    onError: (error) => setNotice(getErrorMessage(error)),
  });

  const status = couponStatus(coupon);
  const free = (options.data?.coupons || []).filter((item) => !item.event);

  const buttonClass =
    "inline-flex items-center justify-center rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60";

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            <MdLocalOffer size={14} /> Cupom do evento
          </p>

          {coupon ? (
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span className="font-mono text-lg font-bold tracking-wide text-slate-900">
                {coupon.code}
              </span>
              <Badge tone={status.tone}>{status.label}</Badge>
              <span className="text-sm text-slate-600">
                {formatCouponDiscount(coupon)} de desconto
              </span>
            </div>
          ) : (
            <p className="mt-1 text-sm text-slate-500">
              Este evento ainda não tem cupom. Com um cupom, quem se cadastrar
              usando o código conta como cliente do evento.
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {coupon ? (
            <>
              <button
                type="button"
                className={buttonClass}
                onClick={() => setHistoryOpen(true)}
              >
                Ver resgates
              </button>
              <button
                type="button"
                className={buttonClass}
                onClick={() => {
                  setFormError("");
                  setMode("edit");
                }}
              >
                Editar cupom
              </button>
              <button
                type="button"
                className={buttonClass}
                disabled={toggle.isLoading}
                onClick={() => toggle.mutate()}
              >
                {coupon.isActive ? "Pausar" : "Ativar"}
              </button>
              <button
                type="button"
                className={buttonClass}
                disabled={detach.isLoading}
                onClick={() => detach.mutate()}
              >
                Soltar do evento
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-xl bg-pink-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-pink-700"
                onClick={() => {
                  setFormError("");
                  setMode("create");
                }}
              >
                Criar cupom
              </button>
              <button
                type="button"
                className={buttonClass}
                onClick={() => {
                  setFormError("");
                  setMode("pick");
                }}
              >
                Usar um cupom que já existe
              </button>
            </>
          )}
        </div>
      </div>

      {coupon ? (
        <dl className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {[
            [
              "Usos",
              coupon.usageLimit != null
                ? `${formatNumber(coupon.usageCount)} de ${formatNumber(coupon.usageLimit)}`
                : formatNumber(coupon.usageCount),
            ],
            ["Resgates", formatNumber(metrics.couponRedemptions)],
            ["Desconto dado", formatMoney(metrics.couponDiscount)],
            [
              "Validade",
              coupon.expiresAt ? `até ${formatDate(coupon.expiresAt)}` : "Sem data final",
            ],
          ].map(([label, value]) => (
            <div
              key={label}
              className="rounded-xl bg-slate-50 px-3 py-2 ring-1 ring-inset ring-slate-100"
            >
              <dt className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                {label}
              </dt>
              <dd className="text-sm font-semibold text-slate-800">{value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {notice ? (
        <p role="alert" className="mt-3 text-xs font-semibold text-rose-700">
          {notice}
        </p>
      ) : null}

      <Modal
        isOpen={mode === "create" || mode === "edit"}
        onClose={close}
        size="lg"
        icon={MdLocalOffer}
        title={mode === "edit" ? "Editar cupom" : "Novo cupom do evento"}
        subtitle={mode === "edit" ? coupon?.code : event.name}
        closeOnBackdrop={false}
        bodyClassName="bg-slate-50/50"
      >
        <CouponForm
          coupon={
            mode === "edit"
              ? coupon
              : { name: event.name, partnerName: event.name }
          }
          onSubmit={(payload) =>
            mode === "edit" ? edit.mutate(payload) : create.mutate(payload)
          }
          onCancel={close}
          isSaving={create.isLoading || edit.isLoading}
          errorMessage={formError}
        />
      </Modal>

      <Modal
        isOpen={mode === "pick"}
        onClose={close}
        size="sm"
        icon={MdLocalOffer}
        title="Usar um cupom que já existe"
        subtitle={event.name}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" className={buttonClass} onClick={close}>
              Cancelar
            </button>
            <button
              type="button"
              disabled={!pickedId || attach.isLoading}
              onClick={() => attach.mutate(Number(pickedId))}
              className="rounded-xl bg-pink-600 px-4 py-2 text-sm font-bold text-white hover:bg-pink-700 disabled:opacity-50"
            >
              {attach.isLoading ? "Vinculando..." : "Usar neste evento"}
            </button>
          </div>
        }
      >
        <div className="space-y-3">
          {options.isLoading ? (
            <p className="text-sm text-slate-500">Carregando cupons...</p>
          ) : free.length === 0 ? (
            <p className="text-sm text-slate-500">
              Todos os cupons já estão em algum evento. Crie um novo.
            </p>
          ) : (
            <select
              value={pickedId}
              onChange={(e) => setPickedId(e.target.value)}
              aria-label="Cupom"
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
            >
              <option value="">Escolha o cupom</option>
              {free.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.code} · {formatCouponDiscount(item)}
                  {item.isActive ? "" : " (pausado)"}
                </option>
              ))}
            </select>
          )}
          {formError ? (
            <p role="alert" className="text-sm font-semibold text-rose-700">
              {formError}
            </p>
          ) : null}
        </div>
      </Modal>

      <Drawer
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        className="w-full md:w-[560px]"
        title={coupon ? `Resgates do cupom ${coupon.code}` : "Resgates"}
      >
        {historyOpen ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 p-4">
              <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                Desconto concedido
              </p>
              <p className="text-2xl font-bold text-slate-900">
                {formatMoney(history.data?.summary?.discountTotal)}
              </p>
              <p className="text-sm text-slate-500">
                {history.data?.summary?.redemptions ?? 0} resgate(s)
              </p>
            </div>

            {history.isLoading ? (
              <p className="text-sm text-slate-500">Carregando resgates...</p>
            ) : (history.data?.data || []).length === 0 ? (
              <p className="text-sm text-slate-500">
                Este cupom ainda não foi resgatado.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {history.data.data.map((item) => (
                  <li key={item.id} className="py-3">
                    <p className="font-medium text-slate-900">
                      {item.companyName || `Empresa #${item.companyId}`}
                    </p>
                    <p className="text-sm text-slate-500">
                      {item.planId || "—"} · {item.billingCycle || "—"} ·{" "}
                      {formatDate(item.createdAt)}
                    </p>
                    <p className="text-sm text-slate-600">
                      {formatMoney(item.totalBeforeDiscount)} →{" "}
                      <strong>{formatMoney(item.totalAfterDiscount)}</strong> (−
                      {formatMoney(item.discountAmount)})
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </Drawer>
    </Card>
  );
};

export default EventCouponCard;
